import { ClickAwayListener, IconButton, styled } from '@mui/material'
import { ImportExport as ImportExportIcon } from '@mui/icons-material'
import { intl } from '../../../../../i18n'
import { FontSize } from '../../../../../styles/commonStyles'
import { useCallback, useMemo, useRef, useState } from 'react'
import { ColDef } from 'ag-grid-community'
import SortColumnListPopper from './SortColumnListPopper'
import { colorPalette } from '../../../../style/colorPalette'
import { SortedColumnState } from '../../../../model/bulkSheetColumnSortState'
import { BootstrapTooltip } from '../../../../components/tooltips/Tooltip/BootStrapToopTip'

type Props = {
  onDeleteSortedColumn?: (column: string | ColDef<any>) => void
  onDeleteSortedAllColumns: (() => void) | undefined
  onChangeSortColumnState?:
    | ((column: string | ColDef<any>, sort: 'asc' | 'desc' | null) => void)
    | undefined
  sortColumnsState: SortedColumnState[]
  getDisplayColumnName?: (colDef: ColDef) => string | undefined
}

const StyledIconButton = styled(IconButton)<{ active: boolean }>(
  ({ active: hasSortColumns }) => ({
    fontSize: FontSize.MEDIUM,
    fontWeight: 500,
    display: 'flex',
    gap: '1px',
    padding: '5px 10px',
    cursor: hasSortColumns ? 'pointer' : 'default',
  })
)

const StyledImportExportIcon = styled(ImportExportIcon)({
  width: '16px',
  height: '16px',
  color: colorPalette.monotone[4],
  verticalAlign: 'middle',
})

const StyledSeparator = styled('span')({
  color: colorPalette.monotone[3],
})

const StyledSortCount = styled('span')<{ active: boolean }>(({ active }) => ({
  color: active ? colorPalette.skyBlue[7] : colorPalette.monotone[3],
}))

const SortButton = ({
  onDeleteSortedColumn,
  onDeleteSortedAllColumns,
  onChangeSortColumnState,
  sortColumnsState,
  getDisplayColumnName,
}: Props) => {
  const anchorRef = useRef<HTMLButtonElement>(null)
  const [isPopperOpen, setIsPopperOpen] = useState(false)
  const [mouseOver, setMouseOver] = useState(false)

  const hasSortColumns = useMemo(
    () => sortColumnsState.length >= 1,
    [sortColumnsState]
  )
  const handleClose = useCallback(() => setIsPopperOpen(false), [])
  const handleClick = useCallback(
    () =>
      setIsPopperOpen(prev => {
        if (!hasSortColumns) return false
        return !prev
      }),
    [hasSortColumns]
  )

  const handleMouseEnter = useCallback(() => setMouseOver(true), [])
  const handleMouseLeave = useCallback(() => setMouseOver(false), [])

  const isTooltipOpen = useMemo(
    () => mouseOver && !isPopperOpen,
    [mouseOver, isPopperOpen]
  )

  return (
    <>
      <ClickAwayListener onClickAway={handleClose}>
        <div style={{ minWidth: 'fit-content' }}>
          <BootstrapTooltip
            arrow={true}
            title={intl.formatMessage({
              id: 'wbs.header.sorted.table.column.count',
            })}
            open={isTooltipOpen}
            onClose={() => setMouseOver(false)}
            disableInteractive={true}
          >
            <StyledIconButton
              ref={anchorRef}
              disableRipple={true}
              active={hasSortColumns}
              onClick={handleClick}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <StyledImportExportIcon />
              <StyledSortCount active={hasSortColumns}>
                <StyledSeparator>
                  {intl.formatMessage({
                    id: 'wbs.header.filter.sort.label.separator',
                  })}
                </StyledSeparator>
                {hasSortColumns
                  ? sortColumnsState.length.toString()
                  : intl.formatMessage({ id: 'wbs.header.none.label' })}
              </StyledSortCount>
            </StyledIconButton>
          </BootstrapTooltip>
        </div>
      </ClickAwayListener>
      <SortColumnListPopper
        open={isPopperOpen}
        anchorEl={anchorRef.current}
        placement={'bottom-start'}
        onDeleteSortedColumn={onDeleteSortedColumn}
        onDeleteSortedAllColumns={onDeleteSortedAllColumns}
        onChangeSortColumnState={onChangeSortColumnState}
        sortColumnsState={sortColumnsState}
        getDisplayColumnName={getDisplayColumnName}
      />
    </>
  )
}

export default SortButton
